<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Exportação XML e-SUS/SISAB</h4>
            <p class="text-muted mb-0">
              Exporte dados de exames no formato XML compatível com e-SUS/SISAB (fichaAtendimentoIndividual)
            </p>
          </div>
          
          <div class="card-body">
            <div class="row mb-4">
              <div class="col-md-4">
                <label class="form-label">Buscar Exames</label>
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    v-model="searchTerm"
                    placeholder="Nome do paciente, CPF..."
                    @keyup.enter="searchExams"
                  />
                  <button class="btn btn-outline-secondary" type="button" @click="searchExams">
                    <i class="ri-search-line"></i>
                  </button>
                </div>
              </div>
              
              <div class="col-md-3">
                <label class="form-label">Data Inicial</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.startDate"
                  @change="searchExams"
                />
              </div>
              
              <div class="col-md-3">
                <label class="form-label">Data Final</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.endDate"
                  @change="searchExams"
                />
              </div>
              
              <div class="col-md-2 d-flex align-items-end">
                <button
                  class="btn btn-secondary w-100"
                  @click="clearFilters"
                  :disabled="loading"
                >
                  <i class="ri-refresh-line me-1"></i>
                  Limpar
                </button>
              </div>
            </div>

            <!-- Lista de Exames -->
            <div class="row">
              <div class="col-12">
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :checked="allSelected"
                            @change="toggleAllSelection"
                          />
                        </th>
                        <th>ID</th>
                        <th>Paciente</th>
                        <th>CPF</th>
                        <th>Tipo de Exame</th>
                        <th>Data do Exame</th>
                        <th>Status</th>
                        <th>Médico</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loading">
                        <td colspan="9" class="text-center">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Carregando...</span>
                          </div>
                        </td>
                      </tr>
                      <tr v-else-if="exams.length === 0">
                        <td colspan="9" class="text-center text-muted">
                          Nenhum exame encontrado
                        </td>
                      </tr>
                      <tr v-else v-for="exam in exams" :key="exam && exam.id ? exam.id : Math.random()">
                        <td>
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :value="exam && exam.id ? String(exam.id) : null"
                            :checked="selectedExams.includes(exam && exam.id ? exam.id : null)"
                            @change="(e) => {
                              const val = exam && exam.id ? exam.id : null;
                              if (e.target.checked && val !== null && !selectedExams.includes(val)) selectedExams.push(val);
                              else if (!e.target.checked && val !== null) selectedExams = selectedExams.filter(id => id !== val);
                            }"
                          />
                        </td>
                        <td>{{ exam && exam.id ? exam.id : 'N/A' }}</td>
                        <td>
                          <div class="fw-medium">{{ exam && exam.patient_name ? exam.patient_name : 'N/A' }}</div>
                          <small class="text-muted">ID: {{ exam && exam.patient_id ? exam.patient_id : 'N/A' }}</small>
                        </td>
                        <td>{{ formatCPF(exam && exam.patient_cpf ? exam.patient_cpf : '') }}</td>
                        <td>
                          <span class="badge bg-light text-dark">{{ exam && exam.exam_type ? exam.exam_type : 'N/A' }}</span>
                        </td>
                        <td>{{ formatDate(exam && exam.scheduled_date ? exam.scheduled_date : '') }}</td>
                        <td>
                          <span class="badge" :class="getStatusBadgeClass(exam && exam.status ? exam.status : '')">
                            {{ getStatusLabel(exam && exam.status ? exam.status : '') }}
                          </span>
                        </td>
                        <td>
                          <div v-if="exam && exam.doctor_name" class="small">
                            {{ exam.doctor_name }}
                            <div class="text-muted">CRM: {{ exam.doctor_crm || 'N/A' }}</div>
                          </div>
                          <span v-else class="text-muted">N/A</span>
                        </td>
                        <td>
                          <div class="btn-group" role="group">
                            <button
                              class="btn btn-sm btn-outline-info"
                              @click="viewExamDetails(exam)"
                              title="Ver detalhes"
                            >
                              <i class="ri-eye-line"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-primary"
                              @click="openSingleExportModal(exam)"
                              :disabled="isExporting"
                              title="Exportar XML"
                            >
                              <i class="ri-download-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Ações em Lote -->
            <div class="row mt-3" v-if="selectedExams.length > 0">
              <div class="col-12">
                <div class="bg-light p-3 rounded">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="text-muted">
                      {{ selectedExams.length }} exame(s) selecionado(s)
                    </span>
                    <div>
                      <button
                        class="btn btn-primary"
                        @click="showBatchExportModal"
                        :disabled="isExporting"
                      >
                        <i class="ri-download-2-line me-1"></i>
                        Exportar Selecionados
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Paginação -->
            <div class="row mt-3" v-if="pagination.total_pages > 1">
              <div class="col-12">
                <nav aria-label="Navegação de páginas">
                  <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{ disabled: pagination.page === 1 }">
                      <button class="page-link" @click="changePage(pagination.page - 1)">
                        Anterior
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === pagination.page }"
                    >
                      <button class="page-link" @click="changePage(page)">
                        {{ page }}
                      </button>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.page === pagination.total_pages }">
                      <button class="page-link" @click="changePage(pagination.page + 1)">
                        Próximo
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Exportação em Lote -->
    <div class="modal fade" id="batchExportModal" tabindex="-1" ref="batchExportModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Exportação em Lote</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="processBatchExport">
              <div class="mb-3">
                <label class="form-label">Médico Responsável</label>
                <select class="form-select" v-model="batchExport.doctorId" required>
                  <option value="">Selecione um médico</option>
                  <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.id">
                    {{ doctor.name }} - CRM: {{ doctor.crm || 'N/A' }}
                  </option>
                </select>
              </div>
              
              <div class="mb-3">
                <label class="form-label">Formato de Exportação</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="exportFormat"
                    id="formatSingle"
                    value="single"
                    v-model="batchExport.format"
                  />
                  <label class="form-check-label" for="formatSingle">
                    Arquivos individuais (um XML por exame)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="exportFormat"
                    id="formatZip"
                    value="zip"
                    v-model="batchExport.format"
                  />
                  <label class="form-check-label" for="formatZip">
                    Arquivo ZIP com todos os XMLs
                  </label>
                </div>
              </div>
              
              <div class="alert alert-info">
                <i class="ri-information-line me-2"></i>
                Serão exportados {{ selectedExams.length }} exame(s) no formato XML e-SUS/SISAB.
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="processBatchExport"
              :disabled="!batchExport.doctorId || isExporting"
            >
              <span v-if="isExporting" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ri-download-2-line me-1"></i>
              {{ isExporting ? 'Exportando...' : 'Exportar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes do Exame -->
    <div class="modal fade" id="examDetailsModal" tabindex="-1" ref="examDetailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalhes do Exame</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedExamDetails">
            <div class="row">
              <div class="col-md-6">
                <h6 class="text-primary">Informações do Exame</h6>
                <table class="table table-sm table-borderless">
                  <tr>
                    <td class="fw-medium">ID:</td>
                    <td>{{ selectedExamDetails.id }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">Tipo:</td>
                    <td>{{ selectedExamDetails.exam_type || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">Data:</td>
                    <td>{{ formatDate(selectedExamDetails.scheduled_date) }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">Status:</td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(selectedExamDetails.status)">
                        {{ getStatusLabel(selectedExamDetails.status) }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td class="fw-medium">Descrição:</td>
                    <td>{{ selectedExamDetails.description || 'N/A' }}</td>
                  </tr>
                </table>
              </div>
              
              <div class="col-md-6">
                <h6 class="text-primary">Informações do Paciente</h6>
                <table class="table table-sm table-borderless">
                  <tr>
                    <td class="fw-medium">Nome:</td>
                    <td>{{ selectedExamDetails.patient_name || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">CPF:</td>
                    <td>{{ formatCPF(selectedExamDetails.patient_cpf) }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">CNS:</td>
                    <td>{{ selectedExamDetails.patient_cns || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">Data Nasc.:</td>
                    <td>{{ formatDate(selectedExamDetails.patient_birth_date) }}</td>
                  </tr>
                </table>
                
                <h6 class="text-primary mt-3">Médico Responsável</h6>
                <table class="table table-sm table-borderless">
                  <tr>
                    <td class="fw-medium">Nome:</td>
                    <td>{{ selectedExamDetails.doctor_name || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">CRM:</td>
                    <td>{{ selectedExamDetails.doctor_crm || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <td class="fw-medium">CNS:</td>
                    <td>{{ selectedExamDetails.doctor_cns || 'N/A' }}</td>
                  </tr>
                </table>
              </div>
            </div>
            
            <div class="alert alert-info mt-3">
              <i class="ri-information-line me-2"></i>
              <strong>Exportação e-SUS:</strong> Este exame será exportado no formato XML compatível com 
              o e-SUS/SISAB (fichaAtendimentoIndividual). Certifique-se de que os dados do paciente e 
              médico estão completos para uma exportação adequada.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Fechar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="exportFromModal"
              :disabled="isExporting"
            >
              <span v-if="isExporting" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ri-download-line me-1"></i>
              {{ isExporting ? 'Exportando...' : 'Exportar XML' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Exportação Individual -->
    <div class="modal fade" id="singleExportModal" tabindex="-1" ref="singleExportModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Exportar Exame</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Médico Responsável</label>
              <select class="form-select" v-model="singleExport.doctorId" required>
                <option value="">Selecione um médico</option>
                <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.id">
                  {{ doctor.name }} - CRM: {{ doctor.crm || 'N/A' }}
                </option>
              </select>
            </div>
            <div class="alert alert-info">
              <i class="ri-information-line me-2"></i>
              O exame <b>#{{ singleExport.exam?.id }}</b> será exportado no formato XML e-SUS/SISAB.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmSingleExport"
              :disabled="!singleExport.doctorId || isExporting"
            >
              <span v-if="isExporting" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ri-download-line me-1"></i>
              {{ isExporting ? 'Exportando...' : 'Exportar XML' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from '@/components/page-header.vue'
import { fhirExportService } from '@/api/fhir.js'
import { userService } from '@/api/users.js'
import { Modal } from 'bootstrap'

export default {
  name: 'ExportESUS',
  components: {
    Layout,
    PageHeader
  },
  data() {
    return {
      title: 'Exportação e-SUS XML',
      items: [
        {
          text: 'Administração',
          href: '/admin'
        },
        {
          text: 'Exportação e-SUS XML',
          active: true
        }
      ],
      
      // Estado da aplicação
      loading: false,
      isExporting: false,
      
      // Busca e filtros
      searchTerm: '',
      filters: {
        startDate: '',
        endDate: ''
      },
      
      // Dados
      exams: [],
      doctors: [],
      selectedExams: [],
      
      // Paginação
      pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0
      },
      
      // Exportação em lote
      batchExport: {
        doctorId: '',
        format: 'single'
      },
      
      // Modal
      batchExportModal: null,
      examDetailsModal: null,
      selectedExamDetails: null,
      // Exportação individual
      singleExportModal: null,
      singleExport: {
        exam: null,
        doctorId: ''
      },
    }
  },
  
  computed: {
    allSelected() {
      return this.exams.length > 0 && this.selectedExams.length === this.exams.length
    },
    
    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.total_pages
      const pages = []
      
      // Mostrar até 5 páginas ao redor da atual
      const start = Math.max(1, current - 2)
      const end = Math.min(total, current + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  },
  
  async mounted() {
    await this.loadInitialData()
    this.initializeModal()
  },
  
  methods: {
    async loadInitialData() {
      try {
        // Carregar exames e médicos em paralelo
        await Promise.all([
          this.loadExams(),
          this.loadDoctors()
        ])
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error)
        this.$showError('Erro ao carregar dados. Tente novamente.')
      }
    },
    
    async loadExams() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.per_page,
          q: this.searchTerm,
          start_date: this.filters.startDate,
          end_date: this.filters.endDate
        }
        const response = await fhirExportService.searchExamsForExport(params)
        let examsArr = []
        if (response.data && Array.isArray(response.data.exams)) {
          examsArr = response.data.exams
        } else if (Array.isArray(response.data)) {
          examsArr = response.data
        } else if (response.data && Array.isArray(response.data.items)) {
          examsArr = response.data.items
        } else if (response.data && response.data.items && typeof response.data.items === 'object') {
          examsArr = Object.values(response.data.items)
        }
        this.exams = Array.isArray(examsArr) ? examsArr : []
        // Atualizar paginação se disponível
        if (response.data && response.data.pagination) {
          this.pagination = { ...this.pagination, ...response.data.pagination }
        }
        // Limpar seleções
        this.selectedExams = []
      } catch (error) {
        console.error('Erro ao carregar exames:', error)
        this.$showError('Erro ao carregar exames')
        this.exams = []
      } finally {
        this.loading = false
      }
    },
    
    async loadDoctors() {
      try {
        const response = await userService.getUsers({ 
          role: 'doctor',
          is_active: true,
          per_page: 100 // Carregar até 100 médicos
        })
        this.doctors = response.data.items || response.data || []
      } catch (error) {
        console.error('Erro ao carregar médicos:', error)
        this.$showError('Erro ao carregar lista de médicos')
        this.doctors = []
      }
    },
    
    async searchExams() {
      this.pagination.page = 1
      await this.loadExams()
    },
    
    clearFilters() {
      this.searchTerm = ''
      this.filters.startDate = ''
      this.filters.endDate = ''
      this.pagination.page = 1
      this.loadExams()
    },
    
    async changePage(page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.pagination.page = page
        await this.loadExams()
      }
    },
    
    toggleAllSelection() {
      if (this.allSelected) {
        this.selectedExams = []
      } else {
        this.selectedExams = this.exams.map(exam => exam.id)
      }
    },
    
    async exportSingleExam(examId) {
      try {
        this.isExporting = true
        // Se o backend aceitar, envie o doctorId como query param ou header
        // Exemplo: `/fhir/export/esus-xml/${examId}?doctor_id=${doctorId}`
        // Aqui mantemos apenas o examId pois o backend atual usa o usuário autenticado
        const response = await fhirExportService.exportESUSXML(examId)
        // Criar download do arquivo
        const blob = new Blob([response.data], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `esus_exam_${examId}.xml`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$showSuccess('XML exportado com sucesso!')
      } catch (error) {
        console.error('Erro na exportação:', error)
        this.$showError('Erro ao exportar XML. Tente novamente.')
      } finally {
        this.isExporting = false
      }
    },
    
    showBatchExportModal() {
      this.batchExportModal.show()
    },
    
    async processBatchExport() {
      if (!this.batchExport.doctorId) {
        this.$showError('Selecione um médico responsável')
        return
      }
      
      try {
        this.isExporting = true
        const totalExams = this.selectedExams.length
        
        if (this.batchExport.format === 'single') {
          // Exportar arquivos individuais
          for (const examId of this.selectedExams) {
            await this.exportSingleExam(examId)
          }
          
          this.batchExportModal.hide()
          this.selectedExams = []
          this.$showSuccess(`${totalExams} arquivo(s) exportado(s) com sucesso!`)
        } else {
          // Implementar exportação ZIP no futuro
          this.$showInfo('Exportação ZIP será implementada em breve')
          this.batchExportModal.hide()
        }
      } catch (error) {
        console.error('Erro na exportação em lote:', error)
        this.$showError('Erro na exportação em lote. Tente novamente.')
      } finally {
        this.isExporting = false
      }
    },
    
    initializeModal() {
      this.batchExportModal = new Modal(this.$refs.batchExportModal)
      this.examDetailsModal = new Modal(this.$refs.examDetailsModal)
      this.singleExportModal = new Modal(this.$refs.singleExportModal)
    },

    openSingleExportModal(exam) {
      this.singleExport.exam = exam
      this.singleExport.doctorId = ''
      this.singleExportModal.show()
    },

    async confirmSingleExport() {
      if (!this.singleExport.exam || !this.singleExport.doctorId) {
        this.$showError('Selecione o exame e o médico responsável')
        return
      }
      await this.exportSingleExam(this.singleExport.exam.id)
      this.singleExportModal.hide()
    },
    
    viewExamDetails(exam) {
      this.selectedExamDetails = exam
      this.examDetailsModal.show()
    },
    
    async exportFromModal() {
      if (this.selectedExamDetails) {
        await this.exportSingleExam(this.selectedExamDetails.id)
        this.examDetailsModal.hide()
      }
    },
    
    // Formatação
    formatCPF(cpf) {
      if (!cpf) return 'N/A'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('pt-BR')
    },
    
    getStatusLabel(status) {
      const labels = {
        scheduled: 'Agendado',
        in_progress: 'Em Andamento',
        completed: 'Concluído',
        cancelled: 'Cancelado'
      }
      return labels[status] || status
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        scheduled: 'bg-warning',
        in_progress: 'bg-info',
        completed: 'bg-success',
        cancelled: 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    }
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  vertical-align: middle;
  text-align: center;
}

.table td {
  vertical-align: middle;
  text-align: center;
}

.table td:first-child,
.table th:first-child {
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  padding-left: 0;
  padding-right: 0;
}

.form-check-input {
  margin: 0 auto;
  display: block;
}

.badge {
  font-size: 0.75rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-check-input:checked {
  background-color: #405189;
  border-color: #405189;
}

.btn-outline-primary:hover {
  background-color: #405189;
  border-color: #405189;
}

.pagination .page-link {
  color: #405189;
}

.pagination .page-item.active .page-link {
  background-color: #405189;
  border-color: #405189;
}
</style>
